<template>
    <div>
        <div
            v-if="configurationSet !== null"
            class="module"
        >
            <header class="module__header">
                <router-link
                    tag="a"
                    class="back-btn"
                    :to="{ name: 'admin-configuration-sets' }"
                >
                    <span class="icon-chevron-left"></span>
                </router-link>
                <h1
                    data-test-id="page-title"
                    class="has-back-btn"
                >
                    Configuration Set History
                </h1>
            </header>
            <section class="module__body">
                <div class="configuration-set-title">
                    <h1>Configuration Set: {{ configurationSet.name }}</h1>
                    <div
                        v-if="configurationSet.encryption_key"
                        class="actions"
                    >
                        <encryption-key-qr-modal :encryption-key-id="configurationSet.encryption_key.id" />
                    </div>
                </div>
                <h2>Details</h2>
                <div class="preview-box configuration-set-preview">
                    <ul>
                        <li>
                            <label>Name</label>
                            <span data-test-id="distrib-preview-name">{{ configurationSet.name }}</span>
                        </li>
                        <li v-if="configurationSet.pli_interval_type !== null">
                            <label>PLI Interval</label>
                            <span data-test-id="distrib-preview-pli-interval">{{ pliIntervalValueDisplay }}</span>
                        </li>
                        <li v-if="configurationSet.pli_interval_type === ConfigurationSetPliIntervalTypes.TIME">
                            <label>Jittering</label>
                            <span data-test-id="distrib-preview-pli-jittering">{{ pliJitteringValueDisplay }}</span>
                        </li>
                        <li>
                            <label>Frequency Set</label>
                            <span data-test-id="distrib-preview-frequency-set">{{
                                configurationSet.frequency_set.name
                            }}</span>
                            <frequency-set-type-badge
                                :type="configurationSet.frequency_set.type"
                                data-test-id="distrib-preview-frequency-set-type"
                            />
                        </li>
                        <li v-if="configurationSet.encryption_key">
                            <label>Default Encryption Key</label>
                            <span data-test-id="distrib-preview-encryption-key">{{
                                configurationSet.encryption_key.name
                            }}</span>
                        </li>
                    </ul>
                </div>
                <div
                    :class="['configuration-set-last-fetched', { inactive: lastFetchedAt === 'N/A' }]"
                    data-test-id="last-fetched-container"
                >
                    Last Fetched: <span data-test-id="last-fetched">{{ lastFetchedAt }}</span>
                </div>
                <index-table
                    :cols="columns"
                    title="Configuration Set History"
                    :model="`configuration-sets/${configurationSetId}/distributions`"
                    createText="Create"
                    noResultsText="No Distributions created"
                    data-test-id="configuration-set-distributions-table"
                    class="distributions"
                    defaultSort="created_at"
                    :defaultDir="sortDirections.DESC"
                    optionsComponent="ConfigurationSetDistributionActions"
                >
                    <template #header>
                        <div></div>
                    </template>
                </index-table>
            </section>
        </div>
    </div>
</template>

<script>
import { isNumber } from 'lodash'
import moment from 'moment-timezone'
import IndexTable from '@/components/IndexTable/index.vue'
import FrequencySetTypeBadge from '@/components/FrequencySetTypeBadge.vue'
import EncryptionKeyQrModal from '@/components/EncryptionKeyQrModal.vue'
import sortDirections from '@/constants/sort-directions'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import canToast from '@/mixins/canToast'
import configurationSetPliIntervalTypes from '@/constants/configuration-set-pli-interval-types.js'
import configurationSetValues from '@/mixins/configurationSetValues.js'
import { formatPliInterval } from '@/utils/pli-interval.js'
import ConfigurationSetPliIntervalTypes from '../../../../constants/configuration-set-pli-interval-types.js'
import featureFlags from '../../../../constants/feature-flags.js'

export default {
    components: {
        IndexTable,
        FrequencySetTypeBadge,
        EncryptionKeyQrModal,
    },

    mixins: [needsOrganizationSlug, canToast, configurationSetValues],

    data() {
        const columns = [
            {
                value: 'name',
                label: 'Name',
                sortable: true,
                searchable: true,
            },
            {
                value: 'groups',
                label: 'Groups',
                sortable: false,
                searchable: true,
                display: 'name',
            },
            {
                value: 'assets',
                label: 'AdvancedRelays',
                sortable: false,
                searchable: true,
                display: 'name',
            },
            {
                value: 'distribute_at',
                label: 'Distribution Time',
                filter: 'localDateTime',
                sortable: true,
                searchable: true,
            },
            {
                value: 'activate_at',
                label: 'Activation Time',
                filter: 'localDateTime',
                sortable: true,
                searchable: true,
            },
            {
                value: 'expire_at',
                label: 'Expiration Time',
                filter: ['localDateTime', 'nullToNA'],
                sortable: true,
                searchable: true,
            },
            {
                value: 'status',
                label: 'Status',
                sortable: false,
                searchable: false,
                customCell: 'ConfigurationSetDistributionStatusCell',
            },
        ]

        if (!this.$store.getters.hasFeatureFlag(featureFlags.ADVANCED_RELAYS_ENABLED)) {
            const assetIndex = columns.findIndex(({ value }) => value === 'assets')
            columns.splice(assetIndex, 1)
        }

        return {
            sortDirections,
            configurationSetPliIntervalTypes,
            configurationSet: null,
            configurationSetId: this.$route.params.id,
            columns,
        }
    },

    computed: {
        ConfigurationSetPliIntervalTypes() {
            return ConfigurationSetPliIntervalTypes
        },

        pliIntervalValueDisplay() {
            const interval = formatPliInterval(
                this.configurationSet.pli_interval_type,
                this.configurationSet.pli_interval_value
            )

            return interval === null ? 'N/A' : `Every ${interval.label}`
        },

        pliJitteringValueDisplay() {
            const value = this.configurationSet.pli_interval_time_jitter

            if (isNumber(value) && value > 0) {
                return `${value}%`
            }

            return 'None'
        },

        lastFetchedAt() {
            if (this.configurationSet.last_fetch?.created_at) {
                const timezoneString = moment().tz(moment.tz.guess()).format('z')
                const dateString = moment(this.configurationSet.last_fetch.created_at)
                    .local()
                    .format('MM-DD-YYYY HH:mm')

                return `${dateString} ${timezoneString}`
            }

            return 'N/A'
        },
    },

    mounted() {
        this.$http
            .get(`/${this.organizationSlug}/configuration-sets/${this.configurationSetId}`)
            .then((response) => {
                this.configurationSet = response.data
            })
            .catch((error) => {
                this.toast('There was a problem loading data for this page.', 'error')
            })
    },
}
</script>
<style lang="scss">
@use '@sass/_variables.scss' as *;
.module__body {
    & > h1 {
        font-size: 24px;
        margin-bottom: 20px;
    }
    & > h2 {
        margin-bottom: 10px;
    }

    .configuration-set-title {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;

        h1 {
            font-size: 24px;
            margin-bottom: 0;
        }

        .actions {
            display: flex;
            align-items: center;
            font-size: 1.5rem;
        }
    }

    .distributions {
        .module__actions,
        .module__search,
        .module__body {
            padding: 0;
        }
    }

    .configuration-set-preview .p-badge {
        margin-left: 8px;
        vertical-align: middle;
        position: relative;
        top: -2px;
    }

    .configuration-set-last-fetched {
        margin-top: 10px;
        span {
            color: $green;
        }
        &.inactive {
            span {
                color: $light-gray;
            }
        }
    }
}
</style>
